import { format } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'

import { AccessTimeFilled, AutoStories, Error, NavigateBefore, NavigateNext } from '@mui/icons-material'
import { Alert, Box, Button, Chip, Link, Pagination, Typography, useTheme } from '@mui/material'

import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { LiveEventEvolutionEntry } from '../../types/LiveEventCollection'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent } from '../../types/TrackingEvents'
import { getTrackingEventDescriptionSnippet } from '../../utils/trackingEventDescription'
import { LiveEventEvolutionTimelineRow } from '../LiveEventEvolutionTimelineRow/LiveEventEvolutionTimelineRow'
import {
  countEntryInstances,
  filterStartedEntries,
  getEarlierVersionsSummary,
  getPageContainingEvent,
  getLastInstanceActivity,
  getTagSlots,
  isNewEntry,
  isValidTimestamp,
  LiveEventEvolutionScope,
  stripCommonNamePrefix,
} from '../helpers/liveEventEvolutionTimelineHelpers'
import {
  HEAD_LINE_HEIGHT,
  HEAD_NODE_OFFSET,
  ROW_BOTTOM_SPACING,
  SPINE_CENTER,
  SPINE_COLOR,
  SPINE_COLUMN_GAP,
  SPINE_COLUMN_WIDTH,
  SPINE_LINE_LEFT,
  SPINE_NODE_SIZE,
} from '../helpers/liveEventEvolutionTimelineLayout'
import './LiveEventEvolutionTimeline.scss'
import { LiveEventEvolutionTimelineSkeleton } from './LiveEventEvolutionTimelineSkeleton'

// Constants
const AVERAGE_DAYS_PER_MONTH = 30.44
const ITEMS_PER_PAGE = 100

interface LiveEventEvolutionTimelineProps {
  currentTrackedGame?: TrackedGame
  currentEventId: string | undefined
  events: { [eventId: string]: TrackingEvent }
  entries: LiveEventEvolutionEntry[]
  /** FIRST/LATEST/ALL toggle the entries were fetched with; decides what the "Present" line measures. */
  scope?: LiveEventEvolutionScope
  onEventSelect: (event: TrackingEvent) => void
  loading?: boolean
  onScrollParentTop?: () => void
  /** Blueprint prefix shared by every entry name; stripped from the rows since the tab title already carries it. */
  namePrefix?: string
  /**
   * Opens the Instances tab of the version whose "See instances" action was clicked. Receives that version's event id and, when it is
   * known, its tracking event, so the dialog can move to that event instead of staying on the one it was opened for.
   * When omitted the "See instances" action is hidden.
   */
  onOpenInstancesTab?: (eventId: string, event?: TrackingEvent) => void
  /** Widens the FIRST/LATEST scope to ALL. When omitted the "Show all" footer is hidden. */
  onShowAllVersions?: () => void
  /** Replaces the generic "no data" text when the caller knows why there are no entries (e.g. a filter hid them all). */
  emptyMessage?: string
}

// Helper function to get time difference label between two timestamps
const getTimeDifferenceLabel = (timestamp1: number, timestamp2: number, t: TFunction): string | undefined => {
  const daysDiff = Math.abs(Math.floor((timestamp2 - timestamp1) / (1000 * 60 * 60 * 24)))

  if (daysDiff === 0) {
    return undefined
  }

  // years
  if (daysDiff >= 365) {
    const yearsDiff = daysDiff / 365
    const yearsFixed = parseFloat(yearsDiff.toFixed(1))
    return t('live-events:year', { count: yearsFixed })
  }

  // months
  if (daysDiff >= AVERAGE_DAYS_PER_MONTH) {
    const monthsDiff = daysDiff / AVERAGE_DAYS_PER_MONTH
    const monthsFixed = parseFloat(monthsDiff.toFixed(1))
    return t('live-events:month', { count: monthsFixed })
  }

  // days
  return t('live-events:day', { count: daysDiff })
}

/**
 * Blueprint tab timeline (design 1e): a single-side branching spine where every
 * blueprint version is a compact one-line card that expands in place. Replaces
 * the alternating MUI `<Timeline>` and its hover tooltip.
 */
export const LiveEventEvolutionTimeline: React.FC<LiveEventEvolutionTimelineProps> = ({
  entries,
  scope = 'all',
  onEventSelect,
  currentTrackedGame,
  events,
  currentEventId,
  loading = false,
  onScrollParentTop,
  namePrefix = '',
  onOpenInstancesTab,
  onShowAllVersions,
  emptyMessage,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const userLanguage = useCurrentUserLanguage()
  const liveEventTagGroupsMapByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedEntryKey, setExpandedEntryKey] = useState<string | undefined>(undefined)
  const isInitialMount = useRef(true)

  const sortedEntries = useMemo(() => {
    if (entries.length === 0) return []

    const filteredEntries = filterStartedEntries(entries, Date.now())

    // Sort entries once from latest to oldest
    return [...filteredEntries].sort((a, b) => b.start - a.start)
  }, [entries])

  // Pagination logic
  const shouldShowPagination = sortedEntries.length > ITEMS_PER_PAGE
  const totalPages = Math.ceil(sortedEntries.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedEntries = useMemo(
    () => (shouldShowPagination ? sortedEntries.slice(startIndex, endIndex) : sortedEntries),
    [shouldShowPagination, sortedEntries, startIndex, endIndex]
  )

  const eventKeys = useMemo(() => Object.keys(events), [events])
  const getEntryEvent = useCallback(
    (entry: LiveEventEvolutionEntry): TrackingEvent | undefined => events[`${entry.eventId}-${entry.start}`] || events[entry.eventId],
    [events]
  )
  const tagSlots = useMemo(
    () => getTagSlots(paginatedEntries, scope, (entry) => isNewEntry(entry, getEntryEvent(entry))),
    [paginatedEntries, scope, getEntryEvent]
  )

  // When the entries change, start on the page holding the version the dialog
  // is showing (it starts expanded below), not on page 1 where it may be absent.
  useEffect(() => {
    setCurrentPage(getPageContainingEvent(sortedEntries, currentEventId, ITEMS_PER_PAGE))
  }, [sortedEntries, currentEventId])

  // The row the dialog is currently showing starts expanded.
  useEffect(() => {
    const currentEntry = sortedEntries.find((entry) => entry.eventId === currentEventId)
    setExpandedEntryKey(currentEntry ? `${currentEntry.eventId}-${currentEntry.start}` : undefined)
  }, [currentEventId, sortedEntries])

  // Scroll to top when page changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (onScrollParentTop) {
      onScrollParentTop()
    }
  }, [currentPage, onScrollParentTop])

  const getDescriptionRawHtml = useCallback(
    (entry: LiveEventEvolutionEntry): string | null | undefined => {
      return getTrackingEventDescriptionSnippet(events[entry.eventId], userLanguage)
    },
    [events, userLanguage]
  )

  /** What changed in this version: the entry's own copy, falling back to the event's blueprint data. */
  const getVersionDescription = useCallback(
    (entry: LiveEventEvolutionEntry): string | undefined => {
      const entryDescription = entry.descriptions?.[userLanguage] || entry.descriptions?.en
      if (entryDescription && entryDescription.trim().length > 0) {
        return entryDescription
      }
      const blueprintDescriptions = events[entry.eventId]?.blueprintEvolutionData?.descriptions
      return blueprintDescriptions?.[userLanguage] || blueprintDescriptions?.en
    },
    [events, userLanguage]
  )

  const getDotColor = useCallback(
    (entry: LiveEventEvolutionEntry): string => {
      const tagGroup = events[entry.eventId]?.typeId ? liveEventTagGroupsMapByEventTypeId[events[entry.eventId].typeId] : undefined
      return tagGroup?.colorHex || theme.palette.text.primary
    },
    [events, liveEventTagGroupsMapByEventTypeId, theme.palette.text.primary]
  )

  const handleOpenVersion = useCallback(
    (entry: LiveEventEvolutionEntry) => {
      const event = events[`${entry.eventId}-${entry.start}`] || events[entry.eventId]
      if (event) onEventSelect(event)
    },
    [events, onEventSelect]
  )

  // Only versions the current page provably doesn't reach can be counted; versions
  // hidden by the FIRST/LATEST scope itself are unknown to the client.
  const earlierVersions = useMemo(() => getEarlierVersionsSummary(sortedEntries, endIndex), [sortedEntries, endIndex])

  /** Later pages: gap between this page's newest entry and the last entry of the previous page. */
  const pageGapLabel = useMemo(() => {
    if (currentPage === 1 || paginatedEntries.length === 0 || !isValidTimestamp(paginatedEntries[0].start)) {
      return undefined
    }
    const referenceTimestamp = sortedEntries[startIndex - 1]?.start
    if (!isValidTimestamp(referenceTimestamp)) {
      return undefined
    }
    return getTimeDifferenceLabel(paginatedEntries[0].start, referenceTimestamp, t)
  }, [currentPage, paginatedEntries, sortedEntries, startIndex, t])

  // FIRST rows are version debuts, so the line measures from the newest debut. LATEST/ALL
  // rows are instances, so it measures from when the most recent one ended.
  const presentLabel = useMemo(() => {
    if (sortedEntries.length === 0) {
      return undefined
    }
    const now = Date.now()
    if (scope === 'first') {
      const gap = isValidTimestamp(sortedEntries[0].start) ? getTimeDifferenceLabel(sortedEntries[0].start, now, t) : undefined
      return gap ? t('live-events:live_event_evolution_present_new_version_debuted', { gap }) : undefined
    }
    const activity = getLastInstanceActivity(
      sortedEntries.map((entry) => events[`${entry.eventId}-${entry.start}`]),
      now
    )
    if (!activity) {
      return undefined
    }
    if (activity.activeNow) {
      return t('live-events:live_event_evolution_present_instance_active_now')
    }
    const gap = getTimeDifferenceLabel(activity.endedAt, now, t)
    return gap
      ? t('live-events:live_event_evolution_present_last_instance_active', { gap })
      : t('live-events:live_event_evolution_present_last_instance_active_today')
  }, [events, scope, sortedEntries, t])

  const headLabel = currentPage === 1 ? presentLabel : pageGapLabel

  // Show skeleton when loading
  if (loading) {
    return <LiveEventEvolutionTimelineSkeleton />
  }

  if (!loading && (!currentTrackedGame || entries.length === 0)) {
    return (
      <Alert severity="info" sx={{ marginTop: 2 }}>
        {emptyMessage || t('live-events:live_event_evolution_no_data_available')}
      </Alert>
    )
  }

  return (
    <>
      <Box className="LiveEventEvolutionTimeline">
        {/* SPINE HEAD */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${SPINE_COLUMN_WIDTH}px 1fr`,
            columnGap: SPINE_COLUMN_GAP,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ position: 'relative', minHeight: `${HEAD_LINE_HEIGHT}px` }}>
            <Box
              sx={{
                position: 'absolute',
                left: `${SPINE_LINE_LEFT}px`,
                top: `${HEAD_NODE_OFFSET}px`,
                bottom: 0,
                width: '2px',
                backgroundColor: SPINE_COLOR,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: `${SPINE_CENTER - SPINE_NODE_SIZE / 2}px`,
                top: `${HEAD_NODE_OFFSET - SPINE_NODE_SIZE / 2}px`,
                width: `${SPINE_NODE_SIZE}px`,
                height: `${SPINE_NODE_SIZE}px`,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccessTimeFilled sx={{ fontSize: '13px', color: 'common.white' }} />
            </Box>
          </Box>

          {/* Same height as the head node so the chip and the gap label sit on its axis. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: `${HEAD_LINE_HEIGHT}px`, mb: ROW_BOTTOM_SPACING, flexWrap: 'wrap' }}>
            {currentPage === 1 ? (
              <Typography component="span" sx={{ fontSize: '12px', fontWeight: 'bold', color: 'primary.main' }}>
                {t('live-events:present')}
              </Typography>
            ) : (
              <Chip
                label={`${t('common:label_page')} ${currentPage}`}
                color="primary"
                size="small"
                className="LiveEventEvolutionTimelinePageChip"
                // The global MuiChip override carries an 8px bottom margin, which
                // pushes the chip off the head's axis when it is centered.
                sx={{ m: 0 }}
                icon={<AutoStories sx={{ color: 'white', fontSize: '12px', marginLeft: '8px' }} />}
              />
            )}
            {headLabel && (
              <Typography component="span" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {currentPage === 1 ? `· ${headLabel}` : headLabel}
              </Typography>
            )}
          </Box>
        </Box>

        {/* NO ENTRIES */}
        {paginatedEntries.length === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: `${SPINE_COLUMN_WIDTH}px 1fr`, columnGap: SPINE_COLUMN_GAP, alignItems: 'center' }}>
            <Box />
            <Box>
              <Chip label={t('live-events:live_event_evolution_no_entries')} color="error" icon={<Error sx={{ color: 'white', fontSize: '16px' }} />} />
            </Box>
          </Box>
        )}

        {/* PAST EVENTS */}
        {paginatedEntries.map((entry) => {
          const entryKey = `${entry.eventId}-${entry.start}`
          return (
            <LiveEventEvolutionTimelineRow
              key={entryKey}
              entry={entry}
              event={getEntryEvent(entry)}
              gameId={currentTrackedGame?.game.id || ''}
              displayName={stripCommonNamePrefix(entry.name, namePrefix)}
              dotColor={getDotColor(entry)}
              isCurrent={entry.eventId === currentEventId}
              isExpanded={expandedEntryKey === entryKey}
              instanceCount={countEntryInstances(eventKeys, entry.eventId)}
              tagSlots={tagSlots}
              scope={scope}
              descriptionRawHtml={getDescriptionRawHtml(entry)}
              versionDescriptionHtml={getVersionDescription(entry)}
              onToggle={() => setExpandedEntryKey((previous) => (previous === entryKey ? undefined : entryKey))}
              onOpenVersion={() => handleOpenVersion(entry)}
              onOpenInstancesTab={onOpenInstancesTab && (() => onOpenInstancesTab(entry.eventId, getEntryEvent(entry)))}
            />
          )
        })}

        {/* EARLIER VERSIONS */}
        {onShowAllVersions && paginatedEntries.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: `${SPINE_COLUMN_WIDTH}px 1fr`, columnGap: SPINE_COLUMN_GAP, alignItems: 'flex-start' }}>
            <Box sx={{ position: 'relative', minHeight: '24px' }}>
              <Box sx={{ position: 'absolute', left: `${SPINE_LINE_LEFT}px`, top: 0, height: '18px', borderLeft: '2px dashed', borderColor: SPINE_COLOR }} />
            </Box>
            <Typography component="div" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
              {earlierVersions.count > 0 && earlierVersions.sinceTimestamp !== undefined && (
                <>
                  {t('live-events:live_event_evolution_earlier_versions', {
                    count: earlierVersions.count,
                    date: format(earlierVersions.sinceTimestamp, 'MMM dd, yyyy'),
                  })}
                  {' · '}
                </>
              )}
              <Link component="button" type="button" underline="hover" onClick={onShowAllVersions} sx={{ fontSize: '11.5px', verticalAlign: 'baseline' }}>
                {t('live-events:live_event_evolution_show_all')}
              </Link>
            </Typography>
          </Box>
        )}
      </Box>

      {/* PAGINATION CONTROLS */}
      {shouldShowPagination && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<NavigateBefore />}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            {t('common:label_previous')}
          </Button>

          <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} color="primary" showFirstButton showLastButton />

          <Button
            variant="outlined"
            endIcon={<NavigateNext />}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            {t('common:label_next')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default LiveEventEvolutionTimeline
